import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import api from '../../services/api';

const emptyLocale = { title: '', body: '' };

export default function AppTermsManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState({ es: emptyLocale, en: emptyLocale });

  const selected = useMemo(
    () => items.find((item) => item._id === selectedId) || null,
    [items, selectedId],
  );
  const locked = selected?.status === 'published';

  const load = async (keepId) => {
    const { data } = await api.get('/app_terms');
    const next = data.items || [];
    setItems(next);
    setCurrentVersion(data.currentVersion || 0);
    const id = keepId || selectedId || next[0]?._id || null;
    setSelectedId(id);
    const row = next.find((item) => item._id === id);
    if (row) {
      setDraft({
        es: { title: row.es?.title || '', body: row.es?.body || '' },
        en: { title: row.en?.title || '', body: row.en?.body || '' },
      });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load terms' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const select = (row) => {
    setSelectedId(row._id);
    setDraft({
      es: { title: row.es?.title || '', body: row.es?.body || '' },
      en: { title: row.en?.title || '', body: row.en?.body || '' },
    });
  };

  const setLocale = (lang, key, value) => {
    setDraft((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [key]: value },
    }));
  };

  const createDraft = async () => {
    try {
      setSaving(true);
      const { data } = await api.post('/app_terms', { es: emptyLocale, en: emptyLocale });
      await load(data._id);
      toast.current?.show({ severity: 'success', summary: 'Draft created' });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Could not create draft' });
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!selected || locked) return;
    try {
      setSaving(true);
      await api.put(`/app_terms/${selected._id}`, draft);
      await load(selected._id);
      toast.current?.show({ severity: 'success', summary: 'Draft saved' });
    } catch (err) {
      const detail = err?.response?.data?.error || 'Could not save';
      toast.current?.show({ severity: 'error', summary: 'Error', detail });
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!selected || locked) return;
    try {
      setSaving(true);
      await api.put(`/app_terms/${selected._id}`, draft);
      await api.post(`/app_terms/${selected._id}/publish`);
      await load(selected._id);
      toast.current?.show({
        severity: 'success',
        summary: 'Published',
        detail: 'Mobile users who have not accepted this version will be asked again.',
      });
    } catch (err) {
      const detail = err?.response?.data?.error || 'Could not publish';
      toast.current?.show({ severity: 'error', summary: 'Error', detail });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected || locked) return;
    try {
      setSaving(true);
      await api.delete(`/app_terms/${selected._id}`);
      setSelectedId(null);
      await load(null);
      toast.current?.show({ severity: 'success', summary: 'Draft deleted' });
    } catch (err) {
      const detail = err?.response?.data?.error || 'Could not delete';
      toast.current?.show({ severity: 'error', summary: 'Error', detail });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <div className="grid mb-3">
          <div className="col-8">
            <h2 className="m-0 main-text font-bold">App terms</h2>
            <p className="mt-2 mb-0" style={{ color: '#6b7280' }}>
              Spanish and English live on the same version. Publishing a new version asks every mobile user who has not accepted it yet.
              Current published version: <strong>{currentVersion || 'none'}</strong>
            </p>
          </div>
          <div className="col-4 flex justify-content-end align-items-start">
            <Button
              label="New version"
              icon="pi pi-plus"
              className="p-button-sm"
              loading={saving}
              disabled={loading}
              onClick={createDraft}
            />
          </div>
        </div>

        <div className="grid">
          <div className="col-12 md:col-3">
            {items.length === 0 && !loading && <p>No versions yet.</p>}
            {items.map((item) => {
              const active = item._id === selectedId;
              const label = item.status === 'published' ? `Version ${item.version}` : 'Draft';
              return (
                <button
                  key={item._id}
                  type="button"
                  onClick={() => select(item)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    marginBottom: 8,
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: active ? '2px solid #0f766e' : '1px solid #e5e7eb',
                    background: active ? '#f0fdfa' : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <strong>{label}</strong>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {item.status === 'published'
                      ? `${item.acceptedCount || 0} accepted`
                      : 'Not live yet'}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="col-12 md:col-9">
            {!selected && <p>Select a version or create a new one.</p>}
            {selected && (
              <>
                {locked && (
                  <p style={{ color: '#92400e' }}>
                    This version is published and locked. Create a new version to change the text. Users still on an older acceptance will see the latest published version.
                  </p>
                )}
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <h3>Español</h3>
                    <label htmlFor="terms-es-title">Título</label>
                    <InputText
                      id="terms-es-title"
                      className="w-full mb-3"
                      value={draft.es.title}
                      disabled={locked}
                      onChange={(e) => setLocale('es', 'title', e.target.value)}
                    />
                    <label htmlFor="terms-es-body">Texto</label>
                    <InputTextarea
                      id="terms-es-body"
                      className="w-full"
                      rows={18}
                      autoResize
                      value={draft.es.body}
                      disabled={locked}
                      onChange={(e) => setLocale('es', 'body', e.target.value)}
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <h3>English</h3>
                    <label htmlFor="terms-en-title">Title</label>
                    <InputText
                      id="terms-en-title"
                      className="w-full mb-3"
                      value={draft.en.title}
                      disabled={locked}
                      onChange={(e) => setLocale('en', 'title', e.target.value)}
                    />
                    <label htmlFor="terms-en-body">Text</label>
                    <InputTextarea
                      id="terms-en-body"
                      className="w-full"
                      rows={18}
                      autoResize
                      value={draft.en.body}
                      disabled={locked}
                      onChange={(e) => setLocale('en', 'body', e.target.value)}
                    />
                  </div>
                </div>
                {!locked && (
                  <div className="flex gap-2 mt-3">
                    <Button label="Save draft" icon="pi pi-save" loading={saving} onClick={save} />
                    <Button label="Publish" icon="pi pi-check" loading={saving} onClick={publish} />
                    <Button
                      label="Delete draft"
                      icon="pi pi-trash"
                      className="p-button-text p-button-danger"
                      loading={saving}
                      onClick={remove}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
